import React, { useEffect, useLayoutEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import { UserContext, WorkspaceContext, ForecastContext, OrgContext } from '../../objects/Context';
import './Forecast.css';
import './Components/componentStyles.css';
import '../../pages/Dashboards/MoreSelectionMenu.css';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as ResetIcon } from '../../assets/icons/reset-icon.svg';
import { exportReportComponentsToExcel } from '../../services/excelService';
import { downloadExcelFromCashJson, generateExcelBlobFromCashJson } from '../../services/excelModel/excelCFModelServiceBrowser';
import axios from 'axios';
import Popup from '../Popup/Popup';
import TaskDetails from '../../pages/AIDataTeam/TaskDetail/TaskDetails';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import CashFlowChart from './Components/CashFlowChart';
import ForecastTable from './Components/ForecastTable';
import UnifiedCalendar from './Components/UnifiedCalendar';
import { generatePeriods } from './utils/calendarUtils';
import { getGranularityName } from './utils/formatters';

function Forecast({ 
    forecast,  // Changed from 'forecast' to match backend structure
    setForecast,  // Kept for backwards compatibility, actually sets forecast
    components, 
    updateTitle,
    showHeader = true,
    showExportPDF = true,
    componentsLoading = false
}) {
    const { user } = useContext(UserContext);
    const { workspace } = useContext(WorkspaceContext);
    const { activeScenario, triggerForecastReload, setCurrentForecast, setForecasts } = useContext(ForecastContext);
    const { org } = useContext(OrgContext);
    const rightContainerRef = useRef(null);
    const verticalScrollRef = useRef(null);
    const scrollbarTrackRef = useRef(null);
    const scrollbarThumbRef = useRef(null);
    const scrollStateRef = useRef({ left: null, top: 0, hasUserScrolled: false });
    const isDraggingScrollbar = useRef(false);
    const dragStartX = useRef(0);
    const dragStartScrollLeft = useRef(0);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const [showRollForwardConfirm, setShowRollForwardConfirm] = useState(false);
    const [isRollingForward, setIsRollingForward] = useState(false);
    const downloadMenuRef = useRef(null);
    const [showTaskDetails, setShowTaskDetails] = useState(false);
    const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
    const [generatedTaskConfig, setGeneratedTaskConfig] = useState(null);
    const [existingTask, setExistingTask] = useState(null);
    const [isNewTask, setIsNewTask] = useState(true);
    
    // Label column resize state
    const [labelColumnWidth, setLabelColumnWidth] = useState(250);
    const [isResizing, setIsResizing] = useState(false);
    const resizeStartXRef = useRef(0);
    const resizeStartWidthRef = useRef(250);

    // Generate all calendar periods from forecast.calendar (no paging - show all with horizontal scroll)
    const periods = useMemo(() => generatePeriods(forecast?.calendar), [forecast?.calendar]);
    const visiblePeriods = periods; // Show all periods
    const totalPeriods = periods.length;
    const hasForecastData = !!(forecast?.calendar && visiblePeriods?.length > 0);
    const showRefreshOverlay = componentsLoading && hasForecastData;

    const displayComponents = components;
    
    // Generate unified grid template for all components
    const gridTemplateColumns = useMemo(() => {
        if (!visiblePeriods || visiblePeriods.length === 0) return '';
        // Use minmax to allow columns to stretch when there aren't enough to fill the page
        return `var(--label-column-width) repeat(${visiblePeriods.length}, minmax(var(--period-column-width), 1fr))`;
    }, [visiblePeriods]);


    // Label column resize handlers
    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
        resizeStartXRef.current = e.clientX;
        resizeStartWidthRef.current = labelColumnWidth;
    }, [labelColumnWidth]);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            const delta = e.clientX - resizeStartXRef.current;
            const newWidth = Math.min(600, Math.max(120, resizeStartWidthRef.current + delta));
            setLabelColumnWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDownloadMenu && downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
                setShowDownloadMenu(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showDownloadMenu]);

    useEffect(() => {
        scrollStateRef.current = { left: null, top: 0, hasUserScrolled: false };
    }, [forecast?.id, activeScenario]);

    useEffect(() => {
        const horizontalContainer = rightContainerRef.current;
        const verticalContainer = verticalScrollRef.current;
        if (!horizontalContainer && !verticalContainer) return;

        const handleHorizontalScroll = () => {
            if (!rightContainerRef.current) return;
            scrollStateRef.current = {
                ...scrollStateRef.current,
                left: rightContainerRef.current.scrollLeft,
                hasUserScrolled: true
            };
        };

        const handleVerticalScroll = () => {
            if (!verticalScrollRef.current) return;
            scrollStateRef.current = {
                ...scrollStateRef.current,
                top: verticalScrollRef.current.scrollTop,
                hasUserScrolled: true
            };
        };

        horizontalContainer?.addEventListener('scroll', handleHorizontalScroll, { passive: true });
        verticalContainer?.addEventListener('scroll', handleVerticalScroll, { passive: true });

        return () => {
            horizontalContainer?.removeEventListener('scroll', handleHorizontalScroll);
            verticalContainer?.removeEventListener('scroll', handleVerticalScroll);
        };
    }, [hasForecastData]);

    // Set initial scroll position to show only 2 actuals for a fresh forecast view.
    // useLayoutEffect fires after DOM mutation but before the browser paints,
    // so the user never sees the scroll-position-0 state.
    useLayoutEffect(() => {
        const container = rightContainerRef.current;
        if (container && forecast?.calendar?.actual_period_count) {
            if (scrollStateRef.current.hasUserScrolled) return;
            const actualPeriodCount = forecast.calendar.actual_period_count;
            const periodsToScroll = Math.max(0, actualPeriodCount - 2);
            const periodColumnWidth = 100;
            const gridMargin = 12;
            const scrollPosition = (periodsToScroll * periodColumnWidth) + gridMargin;

            container.scrollLeft = scrollPosition;
            scrollStateRef.current = {
                left: scrollPosition,
                top: 0,
                hasUserScrolled: false
            };
        }
    }, [forecast?.id, activeScenario, forecast?.calendar?.actual_period_count]);

    // Custom scrollbar: sync thumb width & position with scroll container
    const updateScrollbarThumb = useCallback(() => {
        const container = rightContainerRef.current;
        const track = scrollbarTrackRef.current;
        const thumb = scrollbarThumbRef.current;
        if (!container || !track || !thumb) return;

        const { scrollWidth, clientWidth, scrollLeft } = container;
        if (scrollWidth <= clientWidth) {
            track.style.display = 'none';
            return;
        }
        track.style.display = 'block';

        const trackWidth = track.clientWidth;
        const thumbWidth = Math.max(40, (clientWidth / scrollWidth) * trackWidth);
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;

        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${thumbLeft}px)`;
    }, []);

    useEffect(() => {
        const container = rightContainerRef.current;
        if (!container) return;

        container.addEventListener('scroll', updateScrollbarThumb);
        const ro = new ResizeObserver(updateScrollbarThumb);
        ro.observe(container);
        // Also watch the inner grid for size changes
        if (container.firstElementChild) ro.observe(container.firstElementChild);

        setTimeout(updateScrollbarThumb, 150);

        return () => {
            container.removeEventListener('scroll', updateScrollbarThumb);
            ro.disconnect();
        };
    }, [updateScrollbarThumb, components]);

    useEffect(() => {
        if (componentsLoading || !hasForecastData) return;

        const container = rightContainerRef.current;
        const verticalContainer = verticalScrollRef.current;
        const { left, top, hasUserScrolled } = scrollStateRef.current;
        if (!hasUserScrolled) return;

        const frameId = window.requestAnimationFrame(() => {
            if (container && typeof left === 'number') {
                container.scrollLeft = left;
            }
            if (verticalContainer && typeof top === 'number') {
                verticalContainer.scrollTop = top;
            }
            updateScrollbarThumb();
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [componentsLoading, hasForecastData, components, updateScrollbarThumb]);

    // Custom scrollbar: drag handlers
    const handleScrollbarThumbDown = useCallback((e) => {
        e.preventDefault();
        isDraggingScrollbar.current = true;
        dragStartX.current = e.clientX;
        dragStartScrollLeft.current = rightContainerRef.current?.scrollLeft || 0;
        scrollbarThumbRef.current?.classList.add('dragging');
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
    }, []);

    const handleScrollbarTrackClick = useCallback((e) => {
        if (e.target === scrollbarThumbRef.current) return;
        const container = rightContainerRef.current;
        const track = scrollbarTrackRef.current;
        if (!container || !track) return;

        const trackRect = track.getBoundingClientRect();
        const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
        container.scrollLeft = clickRatio * (container.scrollWidth - container.clientWidth);
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDraggingScrollbar.current) return;
            const container = rightContainerRef.current;
            const track = scrollbarTrackRef.current;
            if (!container || !track) return;

            const dx = e.clientX - dragStartX.current;
            const trackWidth = track.clientWidth;
            const scrollableWidth = container.scrollWidth - container.clientWidth;
            const thumbWidth = Math.max(40, (container.clientWidth / container.scrollWidth) * trackWidth);
            const maxThumbLeft = trackWidth - thumbWidth;
            const scrollDelta = (dx / maxThumbLeft) * scrollableWidth;
            container.scrollLeft = dragStartScrollLeft.current + scrollDelta;
        };

        const handleMouseUp = () => {
            if (!isDraggingScrollbar.current) return;
            isDraggingScrollbar.current = false;
            scrollbarThumbRef.current?.classList.remove('dragging');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    useEffect(() => {
        console.log('components', JSON.stringify(components, null, 2));
    }, [components]);

    // Create and manage label column shadow
    useEffect(() => {
        let labelColumnShadowElement = null;
        
        const createShadowElement = () => {
            if (verticalScrollRef.current) {
                if (!labelColumnShadowElement) {
                    labelColumnShadowElement = document.createElement('div');
                    labelColumnShadowElement.className = 'label-column-shadow';
                    verticalScrollRef.current.appendChild(labelColumnShadowElement);
                }
            }
        };
        
        const updateShadowVisibility = () => {
            if (rightContainerRef.current && verticalScrollRef.current && labelColumnShadowElement) {
                const scrollContainer = rightContainerRef.current;
                const contentContainer = verticalScrollRef.current;
                const scrollLeft = scrollContainer.scrollLeft;
                const contentHeight = contentContainer.clientHeight;
                
                // Account for the scroll container's margin (5px top + 12px left from .forecast-scroll-container)
                const scrollContainerMarginLeft = 12;
                const scrollContainerMarginTop = 5;
                
                // Label column shadow: show only when scrolled
                const shouldShowLabelShadow = scrollLeft > 0;
                
                if (shouldShowLabelShadow) {
                    labelColumnShadowElement.classList.add('visible');
                } else {
                    labelColumnShadowElement.classList.remove('visible');
                }
                
                // Position the shadow fixed at the label column edge
                // It stays in place relative to the parent (.forecast-content)
                labelColumnShadowElement.style.left = `calc(var(--label-column-width) + ${scrollContainerMarginLeft}px)`;
                labelColumnShadowElement.style.top = `${scrollContainerMarginTop}px - 5px`;
                labelColumnShadowElement.style.height = `calc(100% - ${scrollContainerMarginTop}px)`;
            }
        };

        // Create shadow element
        createShadowElement();
        
        // Check on component mount and when components change
        updateShadowVisibility();
        
        // Add scroll event listener
        const handleScroll = () => {
            updateShadowVisibility();
        };
        
        // Also check on window resize
        const handleResize = () => {
            updateShadowVisibility();
        };
        
        const scrollContainer = rightContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
        }
        window.addEventListener('resize', handleResize);
        
        // Check after a short delay to ensure DOM is fully rendered
        const timeoutId = setTimeout(updateShadowVisibility, 100);
        
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
            
            // Clean up shadow element
            if (labelColumnShadowElement && labelColumnShadowElement.parentNode) {
                labelColumnShadowElement.parentNode.removeChild(labelColumnShadowElement);
            }
        };
    }, [components]);


    ////////////////////////// Reset Scroll Position //////////////////////////

    const resetScrollPosition = () => {
        // Reset horizontal scroll to show 2 actuals
        if (rightContainerRef.current && forecast?.calendar?.actual_period_count) {
            const actualPeriodCount = forecast.calendar.actual_period_count;
            const periodsToScroll = Math.max(0, actualPeriodCount - 2);
            const periodColumnWidth = 100;
            const gridMargin = 12;
            const scrollPosition = (periodsToScroll * periodColumnWidth) + gridMargin;
            rightContainerRef.current.scrollLeft = scrollPosition;
            rightContainerRef.current.scrollTop = 0;  // Reset vertical scroll on the scrollable container
        }
        
        // Reset vertical scroll to top (for safety)
        if (verticalScrollRef.current) {
            verticalScrollRef.current.scrollTop = 0;
        }
    };

    ////////////////////////// Roll Forward //////////////////////////

    const rollForwardStatus = useMemo(() => {
        const startDate = forecast?.calendar?.start_date || forecast?.calendar?.forecast_start;
        if (!startDate) return { canRoll: true, label: 'Roll Forward →', isActive: true };

        const start = new Date(startDate + (startDate.includes('T') ? '' : 'T00:00:00Z'));
        const today = new Date();
        const diffDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));

        if (diffDays < 7) {
            const label = diffDays === 0 ? 'Rolled today' : `Rolled ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            return { canRoll: false, label, isActive: false };
        }
        return { canRoll: true, label: 'Roll Forward →', isActive: true };
    }, [forecast?.calendar?.start_date, forecast?.calendar?.forecast_start]);

    const handleRollForward = async () => {
        if (!rollForwardStatus.canRoll) return;
        setShowRollForwardConfirm(true);
    };

    const confirmRollForward = async () => {
        setShowRollForwardConfirm(false);
        setIsRollingForward(true);
        const previousStatus = forecast?.status;
        const previousError = forecast?.error;
        if (setForecast) setForecast(prev => ({ ...prev, status: 'rolling-forward', error: null }));
        if (typeof setCurrentForecast === 'function') {
            setCurrentForecast(prev => prev ? { ...prev, status: 'rolling-forward', error: null } : prev);
        }
        if (typeof setForecasts === 'function' && forecast?.id) {
            setForecasts(prev => Array.isArray(prev)
                ? prev.map(item => (
                    item.id === forecast.id
                        ? { ...item, status: 'rolling-forward', error: null }
                        : item
                ))
                : prev
            );
        }
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/roll-forward`,
                { forecastId: forecast?.id, userId: user?.id, workspaceId: workspace?.id },
                { withCredentials: true }
            );
            if (response.data?.success && response.data?.forecast && typeof setCurrentForecast === 'function') {
                setCurrentForecast(response.data.forecast);
            }
            if (typeof triggerForecastReload === 'function') triggerForecastReload();
        } catch (err) {
            console.error('Roll forward failed:', err);
            if (setForecast) {
                setForecast(prev => prev ? { ...prev, status: previousStatus, error: previousError } : prev);
            }
            if (typeof setCurrentForecast === 'function') {
                setCurrentForecast(prev => prev ? { ...prev, status: previousStatus, error: previousError } : prev);
            }
            if (typeof setForecasts === 'function' && forecast?.id) {
                setForecasts(prev => Array.isArray(prev)
                    ? prev.map(item => (
                        item.id === forecast.id
                            ? { ...item, status: previousStatus, error: previousError }
                            : item
                    ))
                    : prev
                );
            }
            const msg = err?.response?.data?.error || 'Failed to roll forward. Please try again.';
            alert(msg);
        } finally {
            setIsRollingForward(false);
        }
    };

    ////////////////////////// Excel / PDF Export //////////////////////////

    const fetchCashJsonForExport = useCallback(async () => {
        const forecastId = forecast?.id;
        if (!forecastId) {
            throw new Error('Forecast ID is required');
        }

        const url = activeScenario
            ? `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/scenarios/${forecastId}/compile/${encodeURIComponent(activeScenario)}`
            : `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/compile/${forecastId}/${user?.id}/${workspace?.id}`;

        const response = await axios.get(url, { withCredentials: true });
        if (!response.data?.success || !response.data?.cashJson) {
            throw new Error('Failed to fetch cash JSON model');
        }

        return response.data.cashJson;
    }, [activeScenario, forecast?.id, user?.id, workspace?.id]);

    const buildExportFilename = useCallback(() => {
        const wsName = (workspace?.name || workspace?.title || 'forecast').replace(/\s+/g, '_');
        return `${wsName}_${new Date().toISOString().slice(0,10)}`;
    }, [workspace?.name, workspace?.title]);

    const buildExportMetadata = useCallback(async () => {
        const clientPortal = org?.clientPortal || {};
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        const logoDownloadUrl = (clientPortal.logoUrl && org?.id && apiBaseUrl)
            ? `${apiBaseUrl}/api/orgs/logo-file/${org.id}`
            : null;
        return {
            workspaceName: workspace?.name || workspace?.title || '',
            forecastName: forecast?.name || forecast?.title || 'Cash Forecast',
            branding: { ...clientPortal, logoDownloadUrl },
        };
    }, [workspace?.name, workspace?.title, forecast?.name, forecast?.title, org?.clientPortal, org?.id]);

    const exportForecastAsPDF = useCallback(async () => {
        try {
            setIsExporting(true);
            const cashJson = await fetchCashJsonForExport();
            const filename = buildExportFilename();
            const workbookBlob = await generateExcelBlobFromCashJson(cashJson, await buildExportMetadata());

            const formData = new FormData();
            formData.append('file', workbookBlob, `${filename}.xlsx`);

            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/export-pdf`,
                formData,
                {
                    withCredentials: true,
                    responseType: 'blob',
                }
            );

            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting PDF:', err);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    }, [buildExportFilename, buildExportMetadata, fetchCashJsonForExport]);
      


   
    return (
        <div className={`forecast-content-container forecast-container`} style={{ '--label-column-width': `${labelColumnWidth}px` }}>
            {showHeader && (
            <div className="forecast-header forecast-header">
                <div className="top-header">
                    <div className="title">
                        <h1 className="forecast-title">
                            Case:&nbsp;
                            <span className="forecast-title-scenario">
                                {activeScenario ? activeScenario.toUpperCase() : 'BASE'}
                            </span>
                        </h1>
                        <h2 className="forecast-subtitle">
                            {forecast?.calendar && totalPeriods > 0 && (
                                <span>
                                    {totalPeriods} {getGranularityName(forecast.calendar.granularity)}
                                    {forecast.calendar.forecast_start && ` • Starting ${new Date(forecast.calendar.forecast_start + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`}
                                </span>
                            )}
                        </h2>
                    </div>
                    <div className="forecast-header-actions">
                        <ResetIcon 
                            className="forecast-header-action" 
                            onClick={resetScrollPosition}
                            title="Reset view"
                        />
                        {showExportPDF && (
                        <div className="export-actions">
                            {isExporting ? (
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                </div>
                            ) : (
                                <div ref={downloadMenuRef} style={{ position: 'relative', display: 'inline-block' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDownloadMenu((prev) => !prev);
                                    }}
                                >
                                    <DownloadIcon className="forecast-header-action" />
                                    {showDownloadMenu && (
                                        <div className="more-selection-menu" style={{ top: 'calc(100% + 8px)', left: 'auto', right: 0, minWidth: '140px', zIndex: 1000 }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div
                                                className="more-option"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowDownloadMenu(false);
                                                    exportForecastAsPDF();
                                                }}
                                            >
                                                <span>Download to PDF</span>
                                            </div>
                                            <div
                                                className="more-option"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    setShowDownloadMenu(false);
                                                    try {
                                                        setIsExporting(true);
                                                        const cashJson = await fetchCashJsonForExport();
                                                        const filename = buildExportFilename();
                                                        await downloadExcelFromCashJson(cashJson, filename, await buildExportMetadata());
                                                    } catch (err) {
                                                        console.error('Excel export failed:', err);
                                                        alert('Failed to export Excel. Please try again.');
                                                    } finally {
                                                        setIsExporting(false);
                                                    }
                                                }}
                                            >
                                                <span>Download to Excel</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        )}
                        {forecast?.calendar && (
                            <button
                                className={`roll-forward-btn${rollForwardStatus.isActive ? ' roll-forward-btn--active' : ' roll-forward-btn--inactive'}`}
                                onClick={handleRollForward}
                                disabled={!rollForwardStatus.canRoll || isRollingForward}
                                title={rollForwardStatus.isActive ? 'Roll the forecast forward one period' : rollForwardStatus.label}
                            >
                                {isRollingForward ? 'Rolling...' : rollForwardStatus.label}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            )}
            <div className={`forecast-content forecast-content ${displayComponents?.length > 0 || forecast?.calendar ? 'has-components' : ''}`} ref={verticalScrollRef}>
                {!hasForecastData && componentsLoading ? (
                    <div className="forecast-loading-state">
                        <LoadingScreen message="Loading forecast" />
                    </div>
                ) : forecast?.status === 'failed' ? (
                    <div className="forecast-empty-state failed-state">
                        <div className="failed-icon">!</div>
                        <p>Forecast build failed</p>
                        <p className="subtitle">Something went wrong while building your forecast. Please try again.</p>
                    </div>
                ) : forecast?.status === 'failed_rollforward' ? (
                    <div className="forecast-empty-state failed-state">
                        <div className="failed-icon">!</div>
                        <p>Rollforward failed</p>
                        <p className="subtitle">Something went wrong while rolling forward. Your forecast has been restored to its previous state.</p>
                    </div>
                ) : forecast?.status === 'in-progress' ? (
                    <div className="forecast-empty-state">
                        <p>Forecast not complete.</p>
                        <p className="subtitle">Work with the agent to build the forecast.</p>
                    </div>
                ) : hasForecastData ? (
                    <>
                    {showRefreshOverlay && (
                        <div className="forecast-refresh-overlay">
                            <LoadingScreen message="Refreshing forecast" />
                        </div>
                    )}
                    <div 
                        className={`forecast-resize-handle ${isResizing ? 'active' : ''}`}
                        style={{ left: `${labelColumnWidth + 12}px` }}
                        onMouseDown={handleResizeStart}
                    />
                    <div className="forecast-scroll-container" ref={rightContainerRef}>
                        <div 
                            className="forecast-unified-grid"
                            style={{
                                '--grid-template-columns': gridTemplateColumns
                            }}
                        >
                            {/* Cash Flow Chart (no x-axis labels) */}
                            <div className="forecast-chart-section">
                                <CashFlowChart 
                                    components={displayComponents || []}
                                    visiblePeriods={visiblePeriods}
                                    granularity={forecast?.calendar?.granularity}
                                    gridTemplateColumns={gridTemplateColumns}
                                    actualPeriodCount={forecast?.calendar?.actual_period_count || 0}
                                />
                            </div>
                            
                            {/* Unified Calendar Header */}
                            <UnifiedCalendar
                                visiblePeriods={visiblePeriods}
                                periodStartIndex={0}
                                granularity={forecast?.calendar?.granularity}
                                actualPeriodCount={forecast?.calendar?.actual_period_count || 0}
                                gridTemplateColumns={gridTemplateColumns}
                            />
                            
                            {/* Component Tables (no column headers) */}
                            <div className="forecast-tables-section">
                                {displayComponents && displayComponents.length > 0 && displayComponents.map((component, idx) => (
                                    <div key={component.id || idx} className="forecast-component-wrapper">
                                        <ForecastTable
                                            component={component}
                                            visiblePeriods={visiblePeriods}
                                            gridTemplateColumns={gridTemplateColumns}
                                            actualPeriodCount={forecast?.calendar?.actual_period_count || 0}
                                            forecastId={forecast?.id}
                                            workspaceId={workspace?.id}
                                            calendar={forecast?.calendar || null}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className="forecast-custom-scrollbar"
                        ref={scrollbarTrackRef}
                        onClick={handleScrollbarTrackClick}
                    >
                        <div
                            className="forecast-custom-scrollbar-thumb"
                            ref={scrollbarThumbRef}
                            onMouseDown={handleScrollbarThumbDown}
                        />
                    </div>
                    </>
                ) : (
                    <div className="forecast-empty-state">
                        <p>No forecast data available</p>
                    </div>
                )}
            </div>
            {showRollForwardConfirm && (
                <Popup
                    content={
                        <div className="roll-forward-confirm-card">
                            <h3 className="roll-forward-confirm-title">Roll Forward Forecast?</h3>
                            <p className="roll-forward-confirm-body">
                                This will advance the forecast start date by one period, converting the next projected period into an actuals period. This action cannot be undone.
                            </p>
                            <div className="roll-forward-confirm-actions">
                                <button className="roll-forward-cancel-btn" onClick={() => setShowRollForwardConfirm(false)}>
                                    Cancel
                                </button>
                                <button className="roll-forward-confirm-btn" onClick={confirmRollForward}>
                                    Roll Forward →
                                </button>
                            </div>
                        </div>
                    }
                    onClose={() => setShowRollForwardConfirm(false)}
                />
            )}
            {showTaskDetails && (
                <Popup
                    content={
                        <TaskDetails
                            task={isNewTask ? {
                                ...(generatedTaskConfig && !taskDetailsLoading ? {
                                    objective: generatedTaskConfig.objective || '',
                                    notes: generatedTaskConfig.notes || '',
                                    title: generatedTaskConfig.title || '',
                                } : {}),
                                emailRecipients: [user?.email],
                                sendEmail: true,
                                agent: 'Task Execution Agent',
                                sampleForecastId: forecast?.id,
                                workspaceId: workspace?.id,
                                creatorUserId: user?.id
                            } : existingTask}
                            setModelOpen={(open) => {
                                setShowTaskDetails(open);
                                if (!open) {
                                    setGeneratedTaskConfig(null);
                                    setTaskDetailsLoading(false);
                                }
                            }}
                            isNewTask={isNewTask}
                            defaultLoading={taskDetailsLoading}
                        />
                    }
                    onClose={() => {
                        setShowTaskDetails(false);
                        setGeneratedTaskConfig(null);
                        setTaskDetailsLoading(false);
                    }}
                />
            )}
        </div>
    );
}

export default Forecast;